import {
  ChakraProvider,
  Box,
  Text,
  Grid,
  GridItem,
  VStack,
  Stack,
  HStack,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  extendTheme,
  Spinner,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import Header from '../components/Header'
import { addDays, format, parse, subDays, intervalToDuration, parseISO, differenceInDays } from 'date-fns'
import Sidebar from '../components/Sidebar'
import '../App.css'
import Api from '../api/api'
import AddNewReservation from '../components/AddNewReservation'
import { ChatIcon, EditIcon, PhoneIcon, QuestionIcon } from '@chakra-ui/icons'
import { MultiSelectTheme } from 'chakra-multiselect';
import AllReservations from '../components/AllReservations'
import BlockModal, { describeBlock } from '../components/BlockModal'

function App() {
  const [date, setDate] = useState<string | null>(null)
  const [reservations, setReservations] = useState<Array<any> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dates, setDates] = useState<Array<any> | null>(null)
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timer | null>(null)
  const [reservationsCountInterval, setReservationsCountInterval] = useState<NodeJS.Timer | null>(null)
  const [lastUpdate, setLastUpdate] = useState(new Date())
  const [reservationsCounter, setReservationsCounter] = useState<Array<any> | null>(null)
  const [locations, setLocations] = useState<Array<string>>([])
  const [reservationsLoading, setReservationsLoading] = useState(false)
  const [allReservations, setAllReservations] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedDates, setSelectedDates] = useState<Date[] | string[]>([])
  const [editingReservation, setEditingReservation] = useState<any>(null)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [floorPlan, setFloorPlan] = useState('Restoranas');
  const [ reservationsList, setReservationsList ] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([])
  const [viewingBlock, setViewingBlock] = useState<any>(null)

  const dateGenerator = () => {
    const dateGenerator = []
    let dateCounter = new Date()
    for (let i = 0; i < 14; i++) {
      const active = i === 0
      const name = format(dateCounter, 'EEEE')
      const date = format(dateCounter, 'yyyy-MM-dd')
      dateGenerator.push({
        name: name,
        date: date,
        active: active,
      })
      if (active) {
        setDate(date)
      }
      dateCounter = addDays(dateCounter, 1)
    }


    const startDate = parse(dateGenerator[0].date, 'yyyy-MM-dd', new Date())
    const endDate = parse(dateGenerator[dateGenerator.length - 1].date, 'yyyy-MM-dd', new Date())
    setSelectedDates([startDate, endDate])

    return dateGenerator
  }

  const getReservations = () => {
    Api.getReservations(date)
      .then((result) => {
        setLastUpdate(new Date())
        setReservations(result)
      })
      .catch(() => {
        setError('Error on fetching orders')
      })
      .finally(() => {
        setReservationsLoading(false)
      })
  }

  const getReservationsCount = () => {
    if (dates) {
      Api.getReservationsCount(dates).then((result) => {
        setReservationsCounter(result)
      })
      getBlocks()
    }
  }

  const getBlocks = () => {
    if (dates && dates.length > 0) {
      Api.getBlocks([dates[0].date, dates[dates.length - 1].date]).then((result: any) => {
        setBlocks(Array.isArray(result) ? result : [])
      })
    }
  }

  // --- reservation blocks (holidays / private events / maintenance) ---
  const toMinutes = (t: string | null | undefined) => {
    if (!t) return null
    const parts = t.split(':')
    return parseInt(parts[0]) * 60 + parseInt(parts[1] ?? '0')
  }

  const blockAppliesToDay = (block: any, day: string) =>
    !!day && block.date_from <= day && block.date_to >= day

  // Blocks that cover the given slot (date, time, location, table). A block with
  // no location covers both locations; with no tables, every table.
  const blocksForSlot = (day: string, interval: string, location: string, table: any) => {
    const slotStart = toMinutes(interval) ?? 0
    const slotEnd = slotStart + 30
    return blocks.filter((block) => {
      if (!blockAppliesToDay(block, day)) return false
      if (block.location && block.location !== location) return false
      if (block.tables && block.tables.length > 0 && !block.tables.includes(table.toString())) return false
      const from = toMinutes(block.time_from)
      const to = toMinutes(block.time_to)
      if (from === null && to === null) return true
      return slotStart < (to ?? 24 * 60) && (from ?? 0) < slotEnd
    })
  }

  const dayBlocks = (day: string) => blocks.filter((block) => blockAppliesToDay(block, day))

  // Whole-day, every-location, every-table block = "closed"
  const closedAllDay = (day: string) =>
    dayBlocks(day).find((b) => !b.time_from && !b.time_to && !b.location && !(b.tables && b.tables.length))

  // Reservation time -> the 30-minute grid slot it starts in (08:15 -> 08:00),
  // so off-grid times (phone agent, hand edits) are still drawn.
  const slotOf = (time: string) => {
    const m = toMinutes(time) ?? 0
    const slot = Math.floor(m / 30) * 30
    return `${Math.floor(slot / 60)}:${(slot % 60).toString().padStart(2, '0')}`
  }

  const getLocations = () => {
    Api.getLocations()
      .then((result) => {
        setLocations(result)
      })
      .catch(() => {
        setError('Error on fetching locations')
      })
  }

  useEffect(() => {
    setDates(dateGenerator())
    getLocations()
  }, [])

  useEffect(() => {
    if (date) {
      setReservationsLoading(true)
      getReservations()
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
      setRefreshInterval(setInterval(getReservations, 10000))
    }
  }, [date])

  useEffect(() => {
      getAllReservations()
  }, [allReservations])

  useEffect(() => {
    setTimeout(() => {
      getReservationsCount()
    }, 1000)
    if (reservationsCountInterval) {
      clearInterval(reservationsCountInterval)
    }
    setReservationsCountInterval(setInterval(getReservationsCount, 30 * 1000))
  }, [dates])

  const getAllReservations = () => {
    if (allReservations) {
      setReservationsLoading(true)
      Api.getAllReservations(selectedDates)
        .then((result) => {
          setReservationsList(result)
        })
        .catch(() => {
          setError('Error on fetching orders')
        })
        .finally(() => {
          setReservationsLoading(false)
        })
      }
    }

  const handleDateSelect = (date: string) => {
    if (dates) {
      setDate(date)

      const dateObj = parse(date, 'yyyy-MM-dd', new Date())
      setSelectedDates([dateObj, dateObj])
      const dateIndex = dates.findIndex((dateInfo) => {
        return date === dateInfo.date
      })
      dates.forEach((date) => (date.active = false))
      if (dateIndex !== -1) {
        dates[dateIndex].active = true
      }
    }
  }

  const addDate = (forward = true) => {
    if (dates) {
      if (forward) {
        const lastDay = addDays(parse(dates[dates.length - 1].date, 'yyyy-MM-dd', new Date()), 1)
        const name = format(lastDay, 'EEEE')
        const date = format(lastDay, 'yyyy-MM-dd')
        const dateToPush = {
          name: name,
          date: date,
          active: false,
        }
        setDates((oldDates) => [...(oldDates ?? []), dateToPush])
      } else {
        const firstDate = subDays(parse(dates[0].date, 'yyyy-MM-dd', new Date()), 1)
        const name = format(firstDate, 'EEEE')
        const date = format(firstDate, 'yyyy-MM-dd')
        const dateToPush = {
          name: name,
          date: date,
          active: false,
        }
        setDates((oldDates) => [dateToPush, ...(oldDates ?? [])])
      }
    }
  }

  const handleDateRangeSelect = (dates: any) => {
    setSelectedDates(dates)
    if (dates.length === 2) {

      const start = format(dates[0], 'yyyy-MM-dd')
      const end = format(dates[1], 'yyyy-MM-dd')
      const duration = differenceInDays(dates[1], dates[0])
      const range = []
      if (!duration ) {
        return;
      }
      for (let i = 0; i <= duration; i++) {
        const date = addDays(dates[0], i)
        range.push(date)
      }

      setDates(
        range.map((date: any) => {
          return {
            name: format(date, 'EEEE'),
            date: format(date, 'yyyy-MM-dd'),
            active: false,
          }
        })
      )
    }
  }

  const onReservationCreated = () => {
    getReservations()
    getAllReservations()
  }

  const onBlockChanged = () => {
    getBlocks()
  }

  const restaurantTableConfig = [101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 'CINEMA']
  const teraceTableConfig = [201, 202, 203, 204, 205, 206, 207, 208];

  const generateIntervals = () => {
    const intervals = [];
    for (let hour = 8; hour < 24; hour++) {
      for (let quarter = 0; quarter < 2; quarter++) {
        const minutes = quarter * 30;
        intervals.push(`${hour.toString()}:${minutes.toString().padStart(2, '0')}`);
      }
    }
    return intervals;
  };

  const intervals = generateIntervals();
  const createNewReservation = (location: string, table: any, interval: any) => {
    setEditingReservation({
      location: location,
      table: [table.toString()],
      time: interval,
      date: date ? new Date(date) : null,
      duration: 120,
    });
  }

  const getColSpan = (start: any, duration: any) => {
    const startMinutes = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
    const endMinutes = startMinutes + duration * 60;
    return Math.ceil((endMinutes - startMinutes) / 30);
  };


  const showFloorPlan = (location: string) => {
    setFloorPlan(location);
    onOpen();
  }

  interface IReservation {
    id: number;
    name: string;
    time: string;
    duration: number;
    guests: number;
    phone: string;
    comment: string;
    user: any;
    inquiry: boolean;
    tasting: boolean;
  }

  interface ITable {
    rows: IReservation[];
  }

  const globalReservationsRendered: number[] = []

  // Empty grid cell: clickable to create, or grey-hatched when a block covers it.
  const renderEmptyCell = (key: string, location: string, table: any, interval: string) => {
    const covering = date ? blocksForSlot(date, interval, location, table) : []
    if (covering.length > 0) {
      const block = covering[0]
      return (<GridItem
        key={key}
        bg="gray.300"
        backgroundImage="repeating-linear-gradient(135deg, transparent 0 6px, rgba(0,0,0,0.08) 6px 12px)"
        p={0}
        border="1px"
        borderColor="gray.400"
        position="relative"
        cursor="pointer"
        title={describeBlock(block)}
        onClick={() => setViewingBlock(block)}
      >
        {interval === (block.time_from ? slotOf(block.time_from) : intervals[0]) && (
          <Text fontSize='xs' color='gray.700' px={1} noOfLines={1}>{block.reason ?? 'Blocked'}</Text>
        )}
      </GridItem>)
    }
    return (<GridItem
      key={key}
      bg="white"
      p={0}
      border="1px"
      borderColor="gray.200"
      position="relative"
      cursor="pointer"
      onClick={() => createNewReservation(location, table, interval)}
    ></GridItem>)
  }

  // Reservations for this location that sit on no configured table (phone-agent
  // event inquiries, hand edits). Drawn in an extra "Unassigned" row so nothing
  // the day badge counts is invisible on the grid.
  const unassignedReservations = (location: string, tableConfig: any[]) => {
    const known = tableConfig.map((t) => t.toString())
    return (reservations ?? [])
      .filter((res) => res.location && res.location.location === location)
      .filter((res) => {
        const tables: string[] = Array.isArray(res.table) ? res.table.map((t: any) => t.toString()) : []
        return tables.length === 0 || !tables.some((t) => known.includes(t))
      })
      .sort((a, b) => (toMinutes(a.time) ?? 0) - (toMinutes(b.time) ?? 0))
  }

  const renderUnassignedRow = (location: string, tableConfig: any[]) => {
    const list = unassignedReservations(location, tableConfig)
    if (list.length === 0) {
      return null
    }
    return (
      <>
        <GridItem bg="orange.100" p={1} className="sticky-column">
          <Text textAlign="center" fontSize="sm">No table</Text>
        </GridItem>
        <GridItem colSpan={intervals.length} bg="orange.50" p={1} border="1px" borderColor="orange.200">
          <HStack spacing={2} flexWrap="wrap">
            {list.map((res) => (
              <HStack
                key={`unassigned-${res.id}`}
                bg={res.inquiry ? 'yellow.800' : 'blue.800'}
                color="white"
                px={2}
                py={1}
                borderRadius={4}
                cursor="pointer"
                onClick={() => setEditingReservation(res)}
              >
                <Text fontSize="xs" fontWeight="bold">{res.time}</Text>
                <Text fontSize="xs">{res.name ?? 'No name'}</Text>
                <Text fontSize="xs" bg="purple.500" px={1.5} borderRadius={4}>{res.guests ?? 0}</Text>
                {res.inquiry ? <Text fontSize="xs">inquiry</Text> : null}
              </HStack>
            ))}
          </HStack>
        </GridItem>
      </>
    )
  }

  // Banner above the grids when the selected day is fully closed, or has any block.
  const renderDayBlockBanner = () => {
    if (!date) return null
    const list = dayBlocks(date)
    if (list.length === 0) return null
    const closed = closedAllDay(date)
    return (
      <HStack
        pl={{ base: 4, md: 52 }}
        pr={4}
        pt={4}
        spacing={2}
        flexWrap="wrap"
      >
        {list.map((block) => (
          <HStack
            key={`banner-block-${block.id}`}
            bg={closed && closed.id === block.id ? 'red.600' : 'gray.600'}
            color="white"
            px={3}
            py={1}
            borderRadius={4}
            cursor="pointer"
            onClick={() => setViewingBlock(block)}
          >
            <Text fontSize="sm" fontWeight="bold">
              {closed && closed.id === block.id ? 'CLOSED' : 'BLOCKED'}
            </Text>
            <Text fontSize="sm">{describeBlock(block)}</Text>
          </HStack>
        ))}
      </HStack>
    )
  }

  const renderTableReservations = (table: any, location: string) => {
    const reservationsForTable = (reservations ?? []).filter(
      (res) => res.table.includes(table.toString()) && res.location.location === location)
      .sort((a, b) => {
        const aTime = parseInt(a.time.split(':')[0]) * 60 + parseInt(a.time.split(':')[1]);
        const bTime = parseInt(b.time.split(':')[0]) * 60 + parseInt(b.time.split(':')[1]);
        return aTime - bTime;
      });

    if (reservationsForTable.length === 0) {
      return intervals.map((interval, index) => {
        return renderEmptyCell(`${table}-${interval}-${index}`, location, table, interval)
      })
    }
    const reservationsRendered: number[] = [];

    const intervalsToMap = [...intervals];

    const firstRenderComplete = false;
    let skipBlocks = 0;
    let notRenderedReservation = reservationsForTable.filter((res) => !reservationsRendered.includes(res.id));
    const content = [];
    let renderingIndex = 0;
    while (notRenderedReservation.length > 0) {
      let renderedBlocks = 0;
      if (renderingIndex > 0) {
        intervalsToMap.unshift('00:30');
      }
      const renderedContent = intervalsToMap.map((interval, index) => {
        renderedBlocks++;
        if (skipBlocks > 0) {
          skipBlocks--;
          return null;
        }

        if (interval === '00:30') {
          return (<GridItem
            key={`${table}-${interval}-${index}-${renderingIndex}`}
            bg="white"
            p={0}
            position="relative"
          ></GridItem>)
        }

        const reservationToRender = notRenderedReservation.find((res) => {
          return slotOf(res.time) === interval
        });

        if (!reservationToRender) {
          return renderEmptyCell(`${table}-${interval}-${index}-${renderingIndex}`, location, table, interval);
        } else {
          let colSpan = getColSpan(reservationToRender.time, reservationToRender.duration / 60)
          if (renderedBlocks + colSpan > intervals.length) {
            colSpan = intervals.length - renderedBlocks;
          }
          const renderFullContent = reservationToRender.duration >= 120;
          const renderOnlyGuestCount = reservationToRender.duration <= 30;

          skipBlocks = colSpan - 1;
          reservationsRendered.push(reservationToRender.id);
          const alreadyRendered = globalReservationsRendered.includes(reservationToRender.id);
          globalReservationsRendered.push(reservationToRender.id);

          let color = 'blue.800';
          if (reservationToRender.inquiry) {
            color = 'yellow.800';
          } else if (reservationToRender.tasting) {
            color = 'orange.300';
          }

          return (
            <GridItem
              key={`${table}-${interval}-${index}-${renderingIndex}`}
              colSpan={colSpan}
              bg={color}
              p={alreadyRendered ? 2 : 2}
              py={alreadyRendered ? '19px' : 2}
              borderWidth={alreadyRendered ? 1 : 1}
              borderColor={'gray.400'}
              position='relative'
              zIndex={1}
              overflow='hidden'
              cursor='pointer'
              onClick={() => setEditingReservation(reservationToRender)}
            >
              <VStack alignItems='center' justifyContent='center' spacing={1}>
                {!renderOnlyGuestCount && (
                  <Text fontSize='sm' fontWeight='bold' color='white' lineHeight='1rem'>
                    {reservationToRender.name ?? 'No name'}
                  </Text>
                )}
                {!alreadyRendered && (
                  <HStack>
                    {renderFullContent && (
                      <Text fontSize='xs' bg='green.500' color='white' px={1.5} borderRadius={4}>
                        @ {reservationToRender.time}
                      </Text>
                    )}
                    <Text fontSize='xs' bg='purple.500' color='white' px={1.5} borderRadius={4}>
                      {reservationToRender.guests ?? 0}
                    </Text>

                    {renderFullContent && reservationToRender.phone && (
                      <PhoneIcon cursor='pointer' color='red.500' />
                    )}
                    {renderFullContent && reservationToRender.comment && (
                      <ChatIcon cursor='pointer' color='blue.300' />
                    )}
                    {renderFullContent && reservationToRender.user && (
                      <Text fontSize='xs' bg='orange.500' color='white' px={1.5} borderRadius={4}>
                        by {reservationToRender.user.name}
                      </Text>
                    )}
                  </HStack>
                )}
              </VStack>
            </GridItem>
          )
        }

      })

      content.push(...renderedContent)
      notRenderedReservation = reservationsForTable.filter((res) => !reservationsRendered.includes(res.id));
      renderingIndex++;
    }
    return content;
  }

  const RestaurantCalendar = () => {
    return (
      <VStack spacing={4} alignItems="flex-start" p={4} width="100%" overflowX="auto" pl={{ base: 0, md: 48 }}>
        <HStack onClick={() => showFloorPlan('Restoranas')} cursor="pointer">
          <Text fontSize="xl" fontWeight="bold" ml={4}>
            Restaurant
          </Text>
          <QuestionIcon color="gray.500" ml={1} />
        </HStack>
        <Box>
          <Grid templateColumns={`70px repeat(${intervals.length}, minmax(60px, 1fr))`} gap={1} px={4}>
            <GridItem />
            {intervals.map((interval, index) => (
              <GridItem key={index} bg="gray.100" p={2}>
                <Text textAlign="center">{interval}</Text>
              </GridItem>
            ))}
            {restaurantTableConfig.map((table) => (
              <>
                <GridItem bg="gray.100" p={1} className="sticky-column">
                  <Text textAlign="center">{table}</Text>
                </GridItem>
                {renderTableReservations(table, 'restaurant')}
              </>
            ))}
            {renderUnassignedRow('restaurant', restaurantTableConfig)}
          </Grid>
        </Box>
      </VStack>
    );
  };

  const TerraceCalender = () => {
    return (
      <VStack spacing={4} alignItems="flex-start" p={4} width="100%" overflowX="auto" pl={{ base: 0, md: 48 }}>
        <HStack onClick={() => showFloorPlan('Terasa')} cursor="pointer">
          <Text fontSize="xl" fontWeight="bold" ml={4}>
            Terace
          </Text>
          <QuestionIcon color="gray.500" ml={1} />
        </HStack>
        <Box>
          <Grid templateColumns={`60px repeat(${intervals.length}, minmax(60px, 1fr))`} gap={1} px={4}>
            <GridItem />
            {intervals.map((interval, index) => (
              <GridItem key={index} bg="gray.100" p={2}>
                <Text textAlign="center">{interval}</Text>
              </GridItem>
            ))}
            {teraceTableConfig.map((table) => (
              <>
                <GridItem bg="gray.100" p={1} className="sticky-column" >
                  <Text textAlign="center">{table}</Text>
                </GridItem>
                {renderTableReservations(table, 'terace')}
              </>
            ))}
            {renderUnassignedRow('terace', teraceTableConfig)}
          </Grid>
        </Box>
      </VStack>
    );
  };

  const themeWithSelect = extendTheme({
    components: {
      MultiSelect: {
        ...MultiSelectTheme,
        baseStyle: (props: any) => {
          const baseStyle = MultiSelectTheme.baseStyle(props) as any
          return {
            ...baseStyle,
          }
        },
      },
    },
  })

  const onAllReservationsChange = (value: any) => {
    const checked = value.target.checked;
    setAllReservations(checked)
  };


  const reservationsPage = () => {
    if (reservationsLoading) {
      return (
        <Stack pl={{ base: 0, md: 48 }} w="full" h="90vh" align="center" justify="center">
          <Spinner
            thickness='4px'
            speed='0.65s'
            emptyColor='gray.200'
            color='green.500'
            size='xl'
          />
        </Stack>
      )
    }

    if (allReservations) {
      return (<AllReservations
        reservations={reservationsList}
        dates={dates}
        onSetEditingReservation={(reservation: any) => setEditingReservation(reservation)}
      />)
    }


    return (
      <>
        {renderDayBlockBanner()}
        {RestaurantCalendar()}
        {TerraceCalender()}
      </>
    )
  }

  return (
    <ChakraProvider theme={themeWithSelect}>
      <Header
        lastUpdate={lastUpdate}
        selectedDates={selectedDates}
        onSelectedDates={handleDateRangeSelect}
        allReservations={allReservations}
        onAllReservations={onAllReservationsChange}
      />
      <Stack>
        {!allReservations && (
          <Sidebar
            dates={dates}
            onDaySelect={handleDateSelect}
            addDate={addDate}
            orderCounter={reservationsCounter}
            blockedDays={blocks}
          />
        )}
        {reservationsPage()}
      </Stack>
      <AddNewReservation
        date={date}
        reservationCreated={onReservationCreated}
        locations={locations}
        editingReservation={editingReservation}
        stopEditingReservation={() => setEditingReservation(null)}
      />
      {!allReservations && (
        <BlockModal
          date={date}
          blockChanged={onBlockChanged}
          viewingBlock={viewingBlock}
          stopViewingBlock={() => setViewingBlock(null)}
        />
      )}
      <Modal isOpen={isOpen} onClose={onClose} size='4xl'>
        <ModalOverlay zIndex='9999999' />
        <ModalContent containerProps={{ zIndex: '9999999' }}>
          <ModalHeader>{floorPlan}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Image src={`/assets/tables/${floorPlan}.png`} alt={floorPlan} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </ChakraProvider>
  )
}

export default App
